import cookies from '@buzzfeed/bf-utils/lib/cookies';

/* eslint-disable max-len */
const ABEAGLE_FLIPPER_PARAM = 'abdebug';

/**
 * Keep track of all 3 states so we can throw appropriate errors when experiments are called
 */
const getDefaultExperimentState = () => ({
  declared: {},
  eligible: {},
  returned: {},
  loaded: false
});

// @type ExperimentOptions
const DEFAULT_EXPERIMENT_OPTIONS = {
  rejectErrors: true,
  defaultVariantIfUnbucketed: 'control'
};
const DEFAULT_VARIANT = null;
const ERROR_TYPES = {
  notFound: {
    logError: name => `Experiment "${name}" is not registered`,
    throwError: name => ({
      type: 'ExperimentNotFound',
      name
    })
  },
  notEligible: {
    logError: name => `Experiment "${name}" is not eligible`,
    throwError: name => ({
      type: 'ExperimentNotEligible',
      name
    })
  },
  error: {
    logError: (name, error) => `Experiment "${name}" error: ${error}`,
    throwError: (name, error) => ({
      type: 'ExperimentServerError',
      name,
      error
    })
  },
  missing: {
    logError: name => `Experiment "${name}" was not in the API response`,
    throwError: name => ({
      type: 'ExperimentServerMissingResponse',
      name
    })
  }
};

/**
 * Log info in debug mode
 * @param {String} error - error message to log
 */
function logError(error) {
  const queryParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined;
  if (queryParams && queryParams.has(ABEAGLE_FLIPPER_PARAM)) {
    console.debug(error);
  }
}

/**
 * Determine the error to be displayed and how it should be handled
 * @param {Object} errorType - error type from the ERROR_TYPES map
 * @param {String} experimentName - name of the experiment causing the error
 * @param {Boolean} rejectErrors - whether or not error should cause app to fail
 * @param {Object} serverError - optional - error passed through from an upstream call
 */
function handleError(_ref) {
  let {
    errorType,
    experimentName,
    rejectErrors,
    serverError
  } = _ref;
  if (rejectErrors) {
    throw errorType.throwError(experimentName, serverError);
  } else {
    logError(errorType.logError(experimentName, serverError));
    return DEFAULT_VARIANT;
  }
}

/**
 * Call this function once per page during setup to create your environment context
 * @param {String} userId - unique user ID
 * @param {Object} data - page context to use in verifying experiment eligibility
 * @param {String} source - service ID, e.g. 'buzz_web' or 'tasty_web'
 * @param {ExperimentConfigs} experimentConfig - experiments declared for a specific service, usually in a /constants/ab-tests.js file
 * @param {String} abeagleHost - the domain to call for experiments
 * @param {ExperimentTrackFunction} trackFn - optional, calls third-party event tracking based on experiments when they register for the first time for a user
 * @returns {Promise<{declared: {}, eligible: {}, returned: {}, loaded: boolean}>}
 */
async function fetchExperiments(_ref2) {
  let {
    userId,
    data,
    source,
    experimentConfig,
    abeagleHost,
    trackFn
  } = _ref2;
  // track all 3 for appropriate error returns;
  const experiments = getDefaultExperimentState();

  // experiments are missing or invalid
  if (!experimentConfig || !experimentConfig.length) {
    experiments.loaded = true;
    return experiments;
  }
  experimentConfig.forEach(experiment => {
    // list of all experiments
    experiments.declared[experiment.name] = experiment;
    const isEligible = typeof experiment.isEligible === 'boolean' ? experiment.isEligible : experiment.isEligible(data);
    if (isEligible) {
      experiments.eligible[experiment.name] = experiment;
    }
  });
  const queryParams = new URLSearchParams();
  queryParams.append('experiment_names', Object.keys(experiments.eligible).join(';'));
  queryParams.append('user_id', userId);
  queryParams.append('source', source);
  queryParams.toString();
  experiments.returned = await fetch(`${abeagleHost}/public/v3/experiment_variants?${queryParams.toString()}`).then(response => {
    return response.json();
  }).catch(err => {
    logError(err);
    return {};
  });
  if (trackFn) {
    trackVariants(experiments.returned, trackFn);
  }

  /**
   * Overwrite eligible experiments with returned data instead of declared data, so we know their value
   */
  Object.keys(experiments.returned).forEach(experimentName => {
    const declared = experiments.declared[experimentName];
    if (!declared) {
      return;
    }
    const returned = experiments.returned[experimentName];
    if (!returned.error && experiments.eligible[experimentName]) {
      experiments.eligible[experimentName] = experiments.returned[experimentName];
    }
  });
  experiments.loaded = true;
  return experiments;
}

/**
 * Parse existing ExperimentState to determine if experiment is enabled
 * @param {ExperimentState} experiments - current experiment state
 * @param {String} experimentName - specific experiment to check
 * @param {ExperimentOptions} options
 * @returns {*} - Enabled variant if valid experiment, may throw error based on options
 */
function getExperimentVariant() {
  let experiments = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getDefaultExperimentState();
  let experimentName = arguments.length > 1 ? arguments[1] : undefined;
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  // apply defaults to options supplied
  const {
    rejectErrors,
    defaultVariantIfUnbucketed
  } = {
    ...DEFAULT_EXPERIMENT_OPTIONS,
    ...options
  };
  const queryParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined;

  // if query param in specific format exists, use that to override variant (for debugging/testing)
  if (queryParams && queryParams.has(`abeagle_${experimentName}`) && queryParams.has(ABEAGLE_FLIPPER_PARAM)) {
    return queryParams.get(`abeagle_${experimentName}`);
  }
  if (!experiments.loaded) {
    return null;
  }

  // first make sure this experiment was declared
  if (!experiments.declared[experimentName]) {
    return handleError({
      errorType: ERROR_TYPES.notFound,
      experimentName,
      rejectErrors
    });
  }

  // next check eligibility
  if (!experiments.eligible[experimentName]) {
    return handleError({
      errorType: ERROR_TYPES.notEligible,
      experimentName,
      rejectErrors
    });
  }

  // make sure we got this experiment from the server
  if (!experiments.returned[experimentName]) {
    return handleError({
      errorType: ERROR_TYPES.missing,
      experimentName,
      rejectErrors
    });
  }

  // make sure this experiment does not have any errors
  if (experiments.returned[experimentName].error) {
    return handleError({
      errorType: ERROR_TYPES.error,
      experimentName,
      rejectErrors,
      serverError: experiments.returned[experimentName].error
    });
  }

  // actually get experiment value
  const variant = experiments.returned[experimentName];
  if (variant.value === DEFAULT_VARIANT && defaultVariantIfUnbucketed !== DEFAULT_VARIANT) {
    return defaultVariantIfUnbucketed;
  }
  return variant.value;
}

/**
 * Helper function to return a boolean for feature flag experiments
 * @param {ExperimentState} experiments
 * @param {String} experimentName - specific experiment to check
 * @param {String} onValue - the variant name that means the experiment is on
 * @returns {*}
 */
function isOn(experiments, experimentName) {
  let onValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'on';
  if (!experiments.loaded) {
    return null;
  }
  const variantValue = getExperimentVariant(experiments, experimentName, {
    rejectErrors: false
  });
  return variantValue === onValue;
}

/**
 *
 */
function getCookieDomain() {
  return cookies.getBuzzfeedSubdomainOrWildcard(window.location.hostname);
}

/**
 * Set tracked experiment cookie.
 * @param {String} name - name of cookie
 * @param {String} value - value of cookie
 * @return {void}
 */
function setCookie(name, value) {
  const domain = window.location.hostname.replace('www', '');
  const cookieDomain = getCookieDomain();
  // Remove the wildcarded cookie, if it exists.
  cookies.remove(name, `.${domain}`);

  // Production cookies last 14 days. Stage and dev cookies last 1 day
  const days = cookieDomain === `www.${domain}` ? 14 : 1;

  // Set the new cookie
  cookies.set({
    name,
    value,
    days,
    domain: cookieDomain
  });
}

/**
 * Store current experiment data in a cookie
 * If experiment has changed and experiment is not resolved, fire a tracking event
 * @param {{ABeagleExperiment}} experiments - returned experiments from ExperimentState
 * @param {ExperimentTrackFunction} trackFn - function that should take 1 ExperimentTrackData param
 * @return {void}
 */
function trackVariants(experiments, trackFn) {
  const cookieDomain = getCookieDomain();
  Object.keys(experiments).forEach(experimentName => {
    const versionCookieName = `${experimentName}_version`;
    let {
      value,
      version,
      error,
      resolved
    } = experiments[experimentName];

    // skip errors, they will be thrown if we try to access this variant
    if (error) {
      return;
    }

    // When experiment is resolved with NULL - save 'control' to user cookies.
    if (resolved) {
      value = value || 'control';
    }

    // At this point NULL value means that a user is not bucketed into any
    // variant.  Nothing should be tracked or saved in that case.  Additionally
    // remove any cookies which may be set for this experiment.
    if (value === null) {
      cookies.remove(experimentName, cookieDomain);
      cookies.remove(versionCookieName, cookieDomain);
      return;
    }

    // Track only if experiment version or value in the response are different
    // from what's stored in cookies
    const isVariantSame = cookies.get(experimentName) === String(value);
    const isVersionSame = cookies.get(versionCookieName) === String(version);
    if (isVariantSame && isVersionSame) {
      return;
    }
    setCookie(experimentName, value);
    setCookie(versionCookieName, version);
    trackFn({
      experiment: experimentName,
      variation: experiments[experimentName]
    });
  });
}

export { ABEAGLE_FLIPPER_PARAM, fetchExperiments, getDefaultExperimentState, getExperimentVariant, isOn, setCookie, trackVariants };
